import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Transition from './Transition';
import Navbar from './Navbar';
import SEO from './SEO';
import ContactSection from './ContactSection';
import Gravity, { MatterBody } from '../fancy/components/physics/gravity';
import '../styles/about-page.css';

gsap.registerPlugin(ScrollTrigger);

// Content migrated 1:1 from the former /about-sections/complete-about.html iframe.
const REVEAL_PARAGRAPHS = [
  'Welcome to the corner of the internet where things get built — not just for the scroll, but for the story. Less portfolio, more working archive of experiments, learnings, and the odd quiet flex.',
  "I'm Sander Kuyken, a graphic designer, UI/UX designer, and 3D/VFX explorer who designs with rhythm and insists every detail earns its place. Curiosity, caffeine, and the urge to ship bold ideas keep me iterating until it clicks.",
];

// keyword -> accent group (matches the pastel ::before colours in about-page.css)
const KEYWORDS = {
  corner: 'corner', learnings: 'learnings', deploy: 'deploy',
  scroll: 'scroll', rhythm: 'rhythm', caffeine: 'caffeine',
  archive: 'archive', detail: 'detail', messing: 'messing',
};

const EXPERIENCE = [
  { year: '2024 — 2025', role: 'Graphic Designer / Motion Graphics Designer', company: 'Cartel Agency' },
  { year: '2022 — 2023', role: 'Graphic Designer', company: 'Topssign' },
  { year: '2022 — 2024', role: 'Dishwasher', company: 'Center Parks' },
];

const EDUCATION = [
  { year: '2025 — 2026', degree: '3D Artist', school: 'SyntraPXL' },
  { year: '2023 — 2025', degree: 'Digital Branding', school: 'PXL University of Applied Sciences & Arts' },
  { year: '2016 — 2023', degree: 'Secondary School – Multimedia', school: 'KTA1 Hasselt / LevelX28' },
];

const SKILLS = [
  'HTML', 'CSS', 'JavaScript', 'GSAP', 'ScrollTrigger', 'Lenis', 'React',
  'Photoshop', 'Illustrator', 'Premiere Pro', 'After Effects', 'Blender', '3DS MAX',
  'Figma', 'Substance Painter', 'Confluence', 'Jira',
];

// Cycling accent colours so the dark timelines + skills carry the pastel palette.
const ACCENTS = ['#b1c1ef', '#f2acac', '#ffdd94'];

const RevealParagraph = ({ text }) => {
  const words = text.split(/\s+/).filter(Boolean);
  return (
    <p>
      {words.map((word, i) => {
        const normalized = word.toLowerCase().replace(/[.,!?;:"—]/g, '');
        const isKeyword = Object.prototype.hasOwnProperty.call(KEYWORDS, normalized);
        return (
          <span key={`${word}-${i}`} className={`word${isKeyword ? ' keyword-wrapper' : ''}`}>
            <span className={isKeyword ? `keyword ${normalized}` : undefined}>{word}</span>
          </span>
        );
      })}
    </p>
  );
};

const AboutPage = () => {
  const revealRef = useRef(null);
  const expRowRefs = useRef([]);
  const eduRowRefs = useRef([]);
  const [activeExp, setActiveExp] = useState(0);
  const [activeEdu, setActiveEdu] = useState(0);

  // Reveal-text: scrubbed word reveal driven by the tall sticky section.
  useEffect(() => {
    const container = revealRef.current;
    if (!container) return;

    const wordEls = Array.from(container.querySelectorAll('.anime-text .word'));
    const total = wordEls.length;
    const highlight = '191, 188, 180';

    const trigger = ScrollTrigger.create({
      trigger: container,
      start: 'top top',
      end: 'bottom bottom',
      scrub: true,
      onUpdate: (self) => {
        const revealProgress = Math.min(1, self.progress / 0.9);
        const overlapWords = 15;
        const totalAnimationLength = 1 + overlapWords / total;
        const timelineScale =
          1 / Math.min(totalAnimationLength, 1 + (total - 1) / total + overlapWords / total);

        wordEls.forEach((word, index) => {
          const span = word.querySelector('span');
          const wordStart = index / total;
          const wordEnd = wordStart + overlapWords / total;
          const adjustedStart = wordStart * timelineScale;
          const adjustedEnd = wordEnd * timelineScale;
          const duration = adjustedEnd - adjustedStart || 1;

          const wordProgress =
            revealProgress <= adjustedStart
              ? 0
              : revealProgress >= adjustedEnd
                ? 1
                : (revealProgress - adjustedStart) / duration;

          word.style.opacity = wordProgress;
          const bgFadeStart = wordProgress >= 0.9 ? (wordProgress - 0.9) / 0.1 : 0;
          word.style.backgroundColor = `rgba(${highlight}, ${Math.max(0, 1 - bgFadeStart)})`;

          const textThreshold = 0.9;
          const textProgress =
            wordProgress >= textThreshold ? (wordProgress - textThreshold) / (1 - textThreshold) : 0;
          if (span) span.style.opacity = Math.pow(textProgress, 0.5);
        });
      },
    });

    const refreshId = setTimeout(() => ScrollTrigger.refresh(), 300);
    return () => {
      clearTimeout(refreshId);
      trigger.kill();
    };
  }, []);

  // Experience + education: highlight the row crossing the viewport centre.
  useEffect(() => {
    const triggers = [];
    expRowRefs.current.forEach((row, index) => {
      if (!row) return;
      triggers.push(
        ScrollTrigger.create({
          trigger: row,
          start: 'top center',
          end: 'bottom center',
          onEnter: () => setActiveExp(index),
          onEnterBack: () => setActiveExp(index),
        })
      );
    });
    eduRowRefs.current.forEach((row, index) => {
      if (!row) return;
      triggers.push(
        ScrollTrigger.create({
          trigger: row,
          start: 'top center',
          end: 'bottom center',
          onEnter: () => setActiveEdu(index),
          onEnterBack: () => setActiveEdu(index),
        })
      );
    });
    return () => triggers.forEach((t) => t.kill());
  }, []);

  return (
    <div className="about-page min-h-screen bg-creme text-mainkleur">
      <SEO
        title="About"
        description="Sander Kuyken's story: 4+ years of professional graphic-design work across branding, motion, 3D / VFX and web/UX. Tools, process and personal milestones."
        path="/about"
      />
      <Navbar />

      {/* 1. EST 2004 header */}
      <section className="about-header font-anton">
        <h1>Est</h1>
        <h1>2004</h1>
      </section>

      {/* 1b. Hero image */}
      <section className="about-hero">
        <div className="about-hero-img">
          <img src="/images/about-pag.jpeg" alt="Sander Kuyken" />
        </div>
      </section>

      {/* 2. Reveal text */}
      <section ref={revealRef} className="anime-text-container" id="reveal-text-section">
        <div className="copy-container">
          <div className="anime-text font-bricolage">
            {REVEAL_PARAGRAPHS.map((text, i) => (
              <RevealParagraph key={i} text={text} />
            ))}
          </div>
        </div>
      </section>

      {/* 3. Experience timeline */}
      <section className="bg-mainkleur text-licht py-24 sm:py-32 lg:py-40" id="experience">
        <div className="max-w-[1422px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12 sm:mb-16">
            <div className="font-anton uppercase text-[18vw] sm:text-[120px] lg:text-[180px] leading-[0.9] tracking-[-4px] lg:tracking-[-8px]">
              <p className="mb-0">EXPE</p>
              <p>RIENCE</p>
            </div>
            <p className="font-space text-licht/70 text-[15px] sm:text-[17px] lg:text-[20px] leading-[1.4] max-w-[638px] lg:text-right">
              From agency work in motion and branding to my own freelance practice, I balance creative vision with hands-on execution—shipping work that feels intentional, energetic, and impeccably crafted.
            </p>
          </div>
          <div className="h-px w-full bg-licht/25 mb-2" />

          <div className="border-t border-licht/10">
            {EXPERIENCE.map((item, index) => {
              const isActive = index === activeExp;
              const accent = ACCENTS[index % ACCENTS.length];
              return (
                <article
                  key={item.company}
                  ref={(el) => (expRowRefs.current[index] = el)}
                  className={`flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between gap-x-8 py-10 sm:py-12 border-b border-licht/10 transition-all duration-300 ${isActive ? 'translate-x-2 sm:translate-x-3' : ''}`}
                >
                  <p
                    className="font-mono text-sm tracking-[0.28em] uppercase transition-colors duration-300 sm:flex-[0_0_24%]"
                    style={{ color: isActive ? accent : 'rgba(228,219,210,0.6)' }}
                  >
                    {item.year}
                  </p>
                  <h4
                    className={`font-bricolage font-bold tracking-tight sm:flex-1 transition-all duration-300 ${isActive ? 'text-licht text-[clamp(2rem,4.4vw,3.1rem)]' : 'text-licht/60 text-[clamp(1.6rem,3.2vw,2.4rem)]'}`}
                  >
                    {item.role}
                  </h4>
                  <p className={`font-space uppercase tracking-[0.08em] text-right sm:flex-[0_0_24%] transition-colors duration-300 ${isActive ? 'text-licht' : 'text-licht/55'}`}>
                    {item.company}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3b. Education timeline */}
      <section className="bg-mainkleur text-licht pt-10 pb-28 sm:pb-36" id="education">
        <div className="max-w-[1422px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 sm:mb-14">
            <div className="font-anton uppercase text-[14vw] sm:text-[80px] lg:text-[112px] leading-[0.9] tracking-[-3px] lg:tracking-[-5px]">
              EDUCATION
            </div>
            <p className="font-space text-licht/70 text-[15px] sm:text-[17px] lg:text-[20px] leading-[1.45] max-w-[560px] lg:text-right">
              A snapshot of the programs and schools that shaped my craft and process.
            </p>
          </div>
          <div className="h-px w-full bg-licht/25 mb-2" />

          <div className="border-t border-licht/10">
            {EDUCATION.map((item, index) => {
              const isActive = index === activeEdu;
              const accent = ACCENTS[index % ACCENTS.length];
              return (
                <article
                  key={item.school}
                  ref={(el) => (eduRowRefs.current[index] = el)}
                  className={`flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between gap-x-8 py-9 sm:py-10 border-b border-licht/10 transition-all duration-300 ${isActive ? 'translate-x-2 sm:translate-x-3' : ''}`}
                >
                  <p
                    className="font-mono text-sm tracking-[0.28em] uppercase transition-colors duration-300 sm:flex-[0_0_24%]"
                    style={{ color: isActive ? accent : 'rgba(228,219,210,0.6)' }}
                  >
                    {item.year}
                  </p>
                  <h4
                    className={`font-bricolage font-bold tracking-tight sm:flex-1 transition-all duration-300 ${isActive ? 'text-licht text-[clamp(1.7rem,3.4vw,2.6rem)]' : 'text-licht/60 text-[clamp(1.4rem,3vw,2.2rem)]'}`}
                  >
                    {item.degree}
                  </h4>
                  <p className={`font-space uppercase tracking-[0.06em] text-right sm:flex-[0_0_24%] transition-colors duration-300 ${isActive ? 'text-licht' : 'text-licht/55'}`}>
                    {item.school}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. Skills — interactive physics playground (native React Gravity) */}
      <section className="bg-creme py-20 sm:py-28">
        <div className="max-w-[1422px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div className="flex flex-col gap-6">
              <h3 className="font-anton uppercase text-[14vw] sm:text-[80px] lg:text-[112px] leading-[0.9] tracking-[-3px] lg:tracking-[-5px]">
                My Skills
              </h3>
              <p className="font-space text-mainkleur/70 text-[16px] sm:text-[18px] lg:text-[20px] leading-[1.5] max-w-[520px]">
                I love building thoughtful, performance-minded work that feels bold yet usable. From motion to micro-copy, it's the little details that make a product feel alive.
              </p>
              <p className="font-space text-mainkleur/45 text-[13px] uppercase tracking-[0.18em]">
                Drag the tools around ↓
              </p>
            </div>

            <div className="relative w-full h-[505px] sm:h-[575px] rounded-2xl border border-dashed border-mainkleur/40 overflow-hidden">
              <Gravity gravity={{ x: 0, y: 1 }} className="w-full h-full">
                {SKILLS.map((skill, i) => {
                  const accent = ACCENTS[i % ACCENTS.length];
                  return (
                    <MatterBody
                      key={skill}
                      bodyType="rectangle"
                      x={`${10 + ((i * 37) % 78)}%`}
                      y={`${5 + (i % 6) * 6}%`}
                      angle={(i % 2 === 0 ? 1 : -1) * 8}
                      matterBodyOptions={{ friction: 0.3, restitution: 0.4 }}
                    >
                      <span
                        className="select-none whitespace-nowrap rounded-full px-4 py-2 font-mono text-[13px] sm:text-sm text-mainkleur shadow-sm"
                        style={{ backgroundColor: accent }}
                      >
                        {skill}
                      </span>
                    </MatterBody>
                  );
                })}
              </Gravity>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Footer — shared with the rest of the site */}
      <ContactSection />
    </div>
  );
};

export default Transition(AboutPage);
